import { ErrorMessage, Field, Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useTariff } from "../context/TariffContext";
import { useUserInfo } from "../context/UserInfoContext";
import EN from "../locales/en";
import Button from "./Button";
import Input from "./Input";
import ErrorIcon from "./icons/ErrorIcon";

const validationSchema = Yup.object({
  email: Yup.string()
    .email(EN.home.email_invalid_error_text)
    .required(EN.home.email_required_error_text),
  firstName: Yup.string().required(EN.home.first_name_required_error_text),
  lastName: Yup.string().required(EN.home.last_name_required_error_text),
});

const UserDetailsForm = ({ setShowPageError }) => {
  const navigate = useNavigate();
  const { setFirstName, setLastName, setEmailAddress, createSession } =
    useUserInfo();
  const { fetchSwitchStatus } = useTariff();

  const submit = async (values) => {
    if (values?.email) {
      setEmailAddress(values.email.toLowerCase());
      setFirstName(values.firstName);
      setLastName(values.lastName);
      try {
        await createSession(
          values.email.toLowerCase(),
          values.firstName,
          values.lastName
        );
      } catch (error) {
        setShowPageError(true);
      }

      const { statusCode, switchStatus } = await fetchSwitchStatus();

      if (statusCode === 200) {
        navigate(`/switch-status/${switchStatus?.switchId}`);
      } else if (statusCode === 404) {
        navigate("/address");
      } else {
        setShowPageError(true);
      }
    }
  };

  return (
    <div className="w-full max-w-lg">
      <Formik
        initialValues={{ email: "", firstName: "", lastName: "" }}
        validationSchema={validationSchema}
        onSubmit={submit}
      >
        {({ isSubmitting, setFieldValue, errors, handleBlur, touched }) => (
          <Form className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Field
                  id="first-name"
                  name="firstName"
                  labelText="First name"
                  placeholder={EN.home.first_name_input_placeholder}
                  as={Input}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("firstName", e.target.value.trim());
                  }}
                  hasErrors={errors.firstName && touched.firstName}
                  disabled={isSubmitting}
                />
                <ErrorMessage name="firstName">
                  {(msg) => (
                    <div className="mt-2 text-red-500 flex items-center">
                      <ErrorIcon className="mr-2" />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>

              <div>
                <Field
                  labelText="Last name"
                  id="last-name"
                  name="lastName"
                  placeholder={EN.home.last_name_input_placeholder}
                  as={Input}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("lastName", e.target.value.trim());
                  }}
                  hasErrors={errors.lastName && touched.lastName}
                  disabled={isSubmitting}
                />
                <ErrorMessage name="lastName">
                  {(msg) => (
                    <div className="mt-2 text-red-500 flex items-center">
                      <ErrorIcon className="mr-2" />
                      {msg}
                    </div>
                  )}
                </ErrorMessage>
              </div>
            </div>

            <div>
              <Field
                id="email-address"
                name="email"
                labelText="Email address"
                placeholder={EN.home.email_input_placeholder}
                as={Input}
                onBlur={(e) => {
                  handleBlur(e);
                  setFieldValue("email", e.target.value.trim().toLowerCase());
                }}
                hasErrors={errors.email && touched.email}
                disabled={isSubmitting}
              />
              <ErrorMessage name="email">
                {(msg) => (
                  <div className="mt-2 text-red-500 flex items-center">
                    <ErrorIcon className="mr-2" />
                    {msg}
                  </div>
                )}
              </ErrorMessage>
            </div>

            <div className="mt-8">
              <Button type="submit" fullWidth disabled={isSubmitting}>
                {isSubmitting ? EN.home.submitting_text : EN.home.continue_text}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UserDetailsForm;
